/* ============================================================
   HHM Cart Drawer — Shopify AJAX Cart API
   Replaces React localStorage cart with real Shopify cart.
   ============================================================ */

(function() {
  'use strict';

  const selectors = {
    drawer:    '[data-cart-drawer]',
    overlay:   '[data-cart-overlay]',
    body:      '[data-cart-body]',
    footer:    '[data-cart-footer]',
    totals:    '[data-cart-totals]',
    count:     '[data-cart-count]',
    trigger:   '[data-cart-drawer-trigger]',
    close:     '[data-cart-drawer-close]',
    loading:   '[data-cart-loading]',
  };

  const drawer  = document.querySelector(selectors.drawer);
  const overlay = document.querySelector(selectors.overlay);
  if (!drawer || !overlay) return;

  // ---- Open / Close ----
  function openDrawer() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    fetchCart();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  overlay.addEventListener('click', closeDrawer);
  document.querySelectorAll(selectors.close).forEach(btn => btn.addEventListener('click', closeDrawer));
  document.querySelectorAll(selectors.trigger).forEach(btn => btn.addEventListener('click', openDrawer));

  // Close on Escape
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

  // ---- Fetch Cart ----
  function fetchCart() {
    const bodyEl   = drawer.querySelector(selectors.body);
    const loadingEl = drawer.querySelector(selectors.loading);
    if (loadingEl) loadingEl.style.display = 'flex';

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => renderCart(cart))
      .catch(() => {
        if (bodyEl) bodyEl.innerHTML = '<p style="text-align:center;padding:2rem;font-family:var(--font-mono);font-size:0.75rem;opacity:0.5;">Unable to load cart. Please refresh.</p>';
      });
  }

  // ---- Render Cart ----
  function renderCart(cart) {
    const bodyEl    = drawer.querySelector(selectors.body);
    const footerEl  = drawer.querySelector(selectors.footer);

    if (!bodyEl) return;

    // Update all cart count badges on page
    document.querySelectorAll(selectors.count).forEach(el => {
      el.textContent = cart.item_count;
      el.classList.toggle('has-items', cart.item_count > 0);
    });

    if (cart.item_count === 0) {
      bodyEl.innerHTML = renderEmptyState();
      if (footerEl) footerEl.style.display = 'none';
      return;
    }

    bodyEl.innerHTML = cart.items.map(item => renderCartItem(item)).join('');
    if (footerEl) footerEl.style.display = 'block';
    renderCartTotals(cart);

    // Bind remove/qty buttons
    bindCartItemEvents(bodyEl);
  }

  // ---- Totals: plain subtotal, or a one-time/recurring breakdown when a
  // subscription line item is present alongside one-time items ----
  function renderCartTotals(cart) {
    const totalsEl = drawer.querySelector(selectors.totals);
    if (!totalsEl) return;

    let oneTimeTotal = 0;
    let recurringTotal = 0;
    cart.items.forEach(item => {
      if (item.selling_plan_allocation) {
        recurringTotal += item.line_price;
      } else {
        oneTimeTotal += item.line_price;
      }
    });

    const taxNoteStyle = 'font-family:var(--font-mono);font-size:0.5625rem;text-transform:uppercase;letter-spacing:0.1em;font-weight:900;color:rgba(2,2,4,0.35);';
    const taxNote = `<p style="${taxNoteStyle}margin-top:0.5rem;">Taxes and shipping calculated at checkout</p>`;

    if (recurringTotal === 0) {
      totalsEl.innerHTML = `
        <div class="hhm-cart-drawer__subtotal-row">
          <span class="hhm-cart-drawer__subtotal-label">Subtotal</span>
          <span class="hhm-cart-drawer__subtotal-price">${formatMoney(cart.total_price)}</span>
        </div>
        ${taxNote}
      `;
      return;
    }

    const labelStyle = 'font-family:var(--font-mono);font-size:0.6875rem;text-transform:uppercase;letter-spacing:0.15em;font-weight:900;';
    const valueStyle = 'font-family:var(--font-mono);font-size:0.875rem;font-weight:900;letter-spacing:0.05em;color:var(--hhm-midnight);';

    let html = '';
    if (oneTimeTotal > 0) {
      html += `<div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:0.625rem;${labelStyle}color:rgba(2,2,4,0.4);"><span>One-Time Total</span><span style="${valueStyle}">${formatMoney(oneTimeTotal)}</span></div>`;
    }
    html += `<div style="display:flex;justify-content:space-between;align-items:baseline;${labelStyle}color:#8CAB24;"><span>Monthly Recurring</span><span style="${valueStyle}color:#8CAB24;">${formatMoney(recurringTotal)}/mo</span></div>`;
    html += `<p style="${taxNoteStyle}margin:0.375rem 0 1.25rem;">* Next monthly subscription billing starts in 30 days.</p>`;
    html += `<hr style="border:none;border-top:1px solid rgba(131,141,86,0.2);margin:0 0 1.25rem;">`;
    html += `<div style="display:flex;justify-content:space-between;align-items:baseline;${labelStyle}color:rgba(2,2,4,0.55);"><span>Due Today Subtotal</span><span style="${valueStyle}font-size:1.375rem;">${formatMoney(cart.total_price)}</span></div>`;
    html += `<p style="${taxNoteStyle}margin:0.5rem 0 0;">Taxes and shipping calculated at checkout</p>`;

    totalsEl.innerHTML = html;
  }

  function renderEmptyState() {
    return `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:3rem 1.5rem;gap:1rem;">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(2,2,4,0.2)" stroke-width="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 01-8 0"></path></svg>
        <p style="font-family:var(--font-cooper-medium);font-size:1.25rem;color:var(--hhm-midnight);">Your cart is empty</p>
        <p style="font-family:var(--font-body);font-size:0.875rem;color:rgba(2,2,4,0.55);max-width:18rem;line-height:1.6;">Add items to your cart to get started with your self-care rituals.</p>
        <button onclick="document.querySelector('[data-cart-drawer-close]').click()" style="font-family:var(--font-mono);font-size:0.6875rem;font-weight:900;text-transform:uppercase;letter-spacing:0.15em;color:var(--hhm-sacred-rose);background:none;border:none;cursor:pointer;border-bottom:1px solid rgba(216,115,124,0.3);padding-bottom:2px;margin-top:0.5rem;">Continue Shopping</button>
      </div>
    `;
  }

  function renderCartItem(item) {
    const imageHtml = item.image
      ? `<img src="${item.image.replace('.jpg', '_120x120.jpg')}" alt="${escapeHtml(item.title)}" style="width:100%;height:100%;object-fit:contain;">`
      : `<div style="width:100%;height:100%;background:var(--hhm-parchment);"></div>`;

    const isSubscription = !!item.selling_plan_allocation;
    const subscriptionBadge = isSubscription
      ? `<span style="display:inline-block;font-family:var(--font-mono);font-size:0.5625rem;text-transform:uppercase;letter-spacing:0.1em;font-weight:900;color:var(--hhm-sacred-rose);border:1px solid rgba(216,115,124,0.35);border-radius:0.375rem;padding:0.25rem 0.5rem;margin-top:0.5rem;">Monthly Sub</span>`
      : '';
    const billedNote = isSubscription
      ? `<span style="display:block;font-family:var(--font-mono);font-size:0.5625rem;text-transform:uppercase;letter-spacing:0.1em;font-weight:900;color:#8CAB24;margin-top:0.25rem;white-space:nowrap;">Billed Monthly</span>`
      : '';

    return `
      <div class="hhm-cart-item" data-cart-item data-item-key="${item.key}">
        <div class="hhm-cart-item__image">${imageHtml}</div>
        <div style="flex:1;display:flex;flex-direction:column;gap:0.5rem;">
          <div style="display:flex;justify-content:space-between;gap:1rem;">
            <div>
              <p class="hhm-cart-item__name">${escapeHtml(item.product_title)}</p>
              ${item.variant_title && item.variant_title !== 'Default Title'
                ? `<p class="hhm-cart-item__size">${escapeHtml(item.variant_title)}</p>`
                : ''}
              ${subscriptionBadge}
            </div>
            <div style="text-align:right;">
              <span class="hhm-cart-item__price">${formatMoney(item.line_price)}</span>
              ${billedNote}
            </div>
          </div>
          <div style="display:flex;align-items:center;justify-content:space-between;margin-top:0.5rem;">
            <div class="hhm-qty" style="min-width:7rem;">
              <button class="hhm-qty__btn" data-qty-change="-1" data-item-key="${item.key}" aria-label="Decrease quantity">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
              <span class="hhm-qty__count">${item.quantity}</span>
              <button class="hhm-qty__btn" data-qty-change="1" data-item-key="${item.key}" aria-label="Increase quantity">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
            </div>
            <button class="hhm-cart-item__remove" data-remove-item data-item-key="${item.key}">Remove</button>
          </div>
        </div>
      </div>
    `;
  }

  function bindCartItemEvents(container) {
    container.querySelectorAll('[data-qty-change]').forEach(btn => {
      btn.addEventListener('click', () => {
        const key   = btn.dataset.itemKey;
        const delta = parseInt(btn.dataset.qtyChange, 10);
        const qtyEl = btn.closest('[data-cart-item]').querySelector('.hhm-qty__count');
        const currentQty = parseInt(qtyEl.textContent, 10);
        const newQty = Math.max(0, currentQty + delta);
        updateItemQty(key, newQty);
      });
    });

    container.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', () => updateItemQty(btn.dataset.itemKey, 0));
    });
  }

  // ---- Cart API ----
  function updateItemQty(key, qty) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(r => r.json())
      .then(cart => renderCart(cart));
  }

  // Public: add to cart (called by variant picker / sticky ATC)
  window.HHMCart = {
    add: function(variantId, qty, properties) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: qty || 1, properties: properties || {} }]
        })
      })
        .then(r => r.json())
        .then(() => {
          openDrawer();
        });
    },
    open: openDrawer,
    close: closeDrawer,
    refresh: fetchCart,
  };

  // ---- Cart note (special instructions) ----
  var noteInput = document.getElementById('CartNote');
  if (noteInput) {
    var noteTimer;
    noteInput.addEventListener('input', function() {
      clearTimeout(noteTimer);
      noteTimer = setTimeout(function() {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: noteInput.value })
        });
      }, 600);
    });
  }

  // Chevron toggle for special instructions details element
  var cartNote = document.querySelector('.hhm-cart-note');
  if (cartNote) {
    cartNote.addEventListener('toggle', function() {
      var chevron = cartNote.querySelector('.hhm-cart-note__chevron');
      if (chevron) chevron.textContent = cartNote.open ? '−' : '+';
    });
  }

  // ---- Helpers ----
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

})();
